import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import DiscoveryPreview from './DiscoveryPreview';

export default function DiscoveryInvitation({ work = false, compact = false, onExplore }) {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const small = isXS || compact;
  const kinds = work ? ['website', 'slides', 'sheet'] : ['game', 'recipe', 'diagram'];
  return <View style={[styles.wrap, compact && styles.compactWrap]} testID="discovery-invitation">
    <Pressable accessibilityRole="button" accessibilityLabel={work ? 'Explore Work ideas' : 'Explore For you'}
      accessibilityHint="Scrolls to the discovery feed below" onPress={onExplore}
      style={({ hovered, pressed }) => [styles.card, small && styles.mobileCard, compact && styles.compactCard, {
        backgroundColor: hovered || pressed ? palette.hover : palette.surface, borderColor: palette.border,
      }]}>
      <View style={[styles.previews, small && styles.mobilePreviews, compact && styles.compactPreviews, { pointerEvents: 'none' }]}>
        {kinds.map((kind, index) => <View key={kind} style={[styles.preview, small && styles.mobilePreview, compact && styles.compactPreview, { transform: [{ rotate: `${(index - 1) * 7}deg` }], zIndex: 3 - index, borderColor: palette.surface }]}>
          <DiscoveryPreview kind={kind} height={compact ? 30 : small ? 40 : 58} />
        </View>)}
      </View>
      <View testID="discovery-invitation-details" style={[styles.details, small ? styles.mobileDetails : styles.desktopDetails]}>
        <View style={styles.copy}>
          <Text size={small ? 'n' : 'l'} weight="medium">{work ? 'Find your next project' : 'See what you can make'}</Text>
          <Text size="s" color="secondary" numberOfLines={2}>{work ? 'Research, documents, websites, and more.' : 'Fresh ideas, useful tools, and places to explore.'}</Text>
        </View>
        <View style={[styles.arrow, { backgroundColor: palette.control }]}><Icon name="arrow-down" size={16} color={palette.ink} /></View>
      </View>
    </Pressable>
  </View>;
}
const styles = StyleSheet.create({
  wrap: { width: '100%', maxWidth: 920, alignSelf: 'center', paddingHorizontal: 20, paddingTop: 12, paddingBottom: 20 },
  card: { borderWidth: 1, borderRadius: 22, paddingHorizontal: 24, paddingVertical: 20, minHeight: 104, flexDirection: 'row', alignItems: 'center', gap: 24 },
  mobileCard: { paddingHorizontal: 18, paddingVertical: 14, minHeight: 128, gap: 10, flexDirection: 'column', alignItems: 'stretch' },
  details: { minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 16 },
  // Yoga resolves a positive flex shorthand plus an auto basis to zero.
  // Keep it only on the horizontal desktop card; mobile copy sizes naturally.
  desktopDetails: { flex: 1 },
  mobileDetails: { width: '100%', flexShrink: 0, gap: 12 },
  compactWrap: { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 8 },
  compactCard: { minHeight: 0, paddingHorizontal: 14, paddingVertical: 10, gap: 8 },
  compactPreviews: { paddingVertical: 2 },
  compactPreview: { width: 56, height: 34 },
  mobilePreviews: { alignSelf: 'center', paddingVertical: 5 },
  mobilePreview: { width: 72, height: 44 },
  copy: { flex: 1, minWidth: 0, gap: 5 },
  previews: { flexDirection: 'row', alignItems: 'center', paddingLeft: 2 },
  preview: { width: 78, height: 62, borderRadius: 10, overflow: 'hidden', borderWidth: 2, marginLeft: -6 },
  arrow: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
});
